import { useState, useEffect } from 'react'
import { SITE_INFO } from '../data/siteData'

/**
 * Henter en hel CMS-samling med fallback til lokale data hvis API fejler eller er tomt.
 */
export function useCMSCollection(collectionName, fallbackData = null) {
  const [data, setData] = useState(() => fallbackData)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true

    async function fetchData() {
      try {
        const res = await fetch(`/_api/public/content/${collectionName}`)
        if (!res.ok) {
          throw new Error(`Failed to load ${collectionName}`)
        }
        const json = await res.json()
        if (isMounted) {
          const items = Array.isArray(json) ? json : json.items || []
          if (items && items.length > 0) {
            setData(items)
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [collectionName])

  return { data: data || fallbackData, loading, error }
}

/**
 * Henter en specifik side fra samlingen 'sider' baseret på slug.
 */
export function useCMSSide(slug, fallbackData = null) {
  const { data: sider, loading, error } = useCMSCollection('sider', null)

  const side = sider?.find((item) => {
    const itemSlug = typeof item.slug === 'object' ? item.slug?.da : item.slug
    const cleanSlug = String(slug || '').replace(/^side-/, '')
    const cleanItemSlug = String(itemSlug || '').replace(/^side-/, '')
    return (
      itemSlug === slug ||
      itemSlug === `side-${slug}` ||
      cleanItemSlug === cleanSlug ||
      item.titel?.toLowerCase() === slug.toLowerCase()
    )
  })

  return {
    side: side || fallbackData,
    loading,
    error,
  }
}

/**
 * Henter virksomhedsoplysninger (kontakt, åbningstider, logoer, adresse)
 * fra samlingen 'virksomhedsoplysninger' med automatisk fallback til SITE_INFO.
 */
export function useVirksomhedsoplysninger() {
  const { data: list, loading, error } = useCMSCollection('virksomhedsoplysninger', null)
  const item = list?.[0]

  const info = item
    ? {
        name: item.firmanavn || SITE_INFO.name,
        tagline: item.tagline || SITE_INFO.tagline,
        phone: item.telefon || SITE_INFO.phone,
        phoneRaw: (item.telefon || SITE_INFO.phone).replace(/\s+/g, ''),
        email: item.email || SITE_INFO.email,
        address: item.adresse || SITE_INFO.address,
        city: item.postnr_by || SITE_INFO.city,
        fullAddress: `${item.adresse || SITE_INFO.address}, ${item.postnr_by || SITE_INFO.city}`,
        cvr: item.cvr || SITE_INFO.cvr,
        mapUrl: item.kort_url || SITE_INFO.mapUrl,
        bookingUrl: item.booking_url || SITE_INFO.bookingUrl,
        aabningstiderHtml: item.aabningstider || null,
        hours: SITE_INFO.hours,
        logos: {
          brandTransparent: item.logo_transparent?.[0] || SITE_INFO.logos.brandTransparent,
          brandFull: item.logo_fuld?.[0] || SITE_INFO.logos.brandFull,
          rabLogo: item.rab_logo?.[0] || SITE_INFO.logos.rabLogo,
          totumLogo: item.totum_logo?.[0] || SITE_INFO.logos.totumLogo,
        },
      }
    : SITE_INFO

  return { info, loading, error }
}
